import React, { Suspense, useContext, useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { List, X, Bug } from 'react-bootstrap-icons';
import ConnectBrokerHeaderButton from '../components/shared/ConnectBrokerHeaderButton';
import AccountDropdown from '../components/shared/AccountDropdown';
import TradeJournalLockoutActions from '../components/TradeJournalLockoutActions';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import DashboardSidebar, { SIDEBAR_WIDTH } from '../components/DashboardSidebar/DashboardSidebar';
import FloatingPrecisionAIChat from '../components/AIChat/FloatingPrecisionAIChat';
import TradeRiskSetupModal from '../components/onboarding/TradeRiskSetupModal';
import TickerInputModal from '../components/StockChart/TickerInputModal';
import useIsMobile from '../hooks/useIsMobile';
import { needsBrokerConnection } from '../utils/onboardingStatus';
import { APP_SHELL_HEADER_HEIGHT } from '../constants/appShellLayout';
import { hasTradePlatformAccess } from '../utils/authAccess';
import {
  LAYOUT_STORAGE_KEYS,
  readLayoutBool,
  writeLayoutBool,
} from '../utils/layoutLocalStorage';

function DashboardOutletFallback({ colors }) {
  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        background: colors?.bgPrimary || '#0b1118',
      }}
      aria-busy="true"
      aria-label="Loading page"
    />
  );
}

/**
 * Dashboard layout for Pro and Essentials.
 * Desktop: header + collapsible sidebar + nested /dashboard/* routes.
 * Mobile: content only — BottomNavTray lives in AuthenticatedMobileShell so it
 * stays mounted when switching between /dashboard/* and /trade.
 */
export default function DashboardShell() {
  const { colors, themeName } = useTheme();
  const { user } = useContext(AuthContext);
  const showConnectBroker = needsBrokerConnection(user);
  const hasTradeAccess = hasTradePlatformAccess(user);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() =>
    readLayoutBool(LAYOUT_STORAGE_KEYS.dashboardSidebarCollapsed, isMobile)
  );

  useEffect(() => {
    writeLayoutBool(LAYOUT_STORAGE_KEYS.dashboardSidebarCollapsed, sidebarCollapsed);
  }, [sidebarCollapsed]);

  const headerBtnStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 6,
    color: colors.text,
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
    outline: 'none',
    flexShrink: 0,
    position: 'relative',
  };

  if (isMobile) {
    // Chrome (bottom nav) is owned by AuthenticatedMobileShell — only page content here.
    return (
      <>
        <Suspense fallback={<DashboardOutletFallback colors={colors} />}>
          <Outlet />
        </Suspense>
        <TradeRiskSetupModal />
        <TickerInputModal />
      </>
    );
  }

  return (
    <div
      className="text-light"
      style={{
        height: '100dvh',
        paddingTop: 'env(safe-area-inset-top)',
        display: 'flex',
        flexDirection: 'column',
        background: colors.bgPrimary,
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          height: APP_SHELL_HEADER_HEIGHT,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px 0 16px',
          backgroundColor: colors.bgHeader,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${colors.borderLight}`,
          zIndex: 1000,
          gap: 6,
          overflow: 'visible',
        }}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate('/home')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/home')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            flexShrink: 0,
            marginLeft: 4,
            padding: '0 6px',
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/favicon.png`}
            alt=""
            role="presentation"
            style={{ height: 30, width: 'auto', objectFit: 'contain', borderRadius: 6 }}
          />
          <span
            key={themeName}
            style={{
              fontWeight: 600,
              fontSize: 18,
              letterSpacing: 0.5,
              background: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              whiteSpace: 'nowrap',
            }}
          >
            PrecisionTrader
          </span>
        </div>

        {user && (
          <div style={{ flexShrink: 0, marginLeft: 12, minWidth: 0, maxWidth: 220, overflow: 'visible', position: 'relative', zIndex: 1100 }}>
            <AccountDropdown dense sizeToContent />
          </div>
        )}

        <div style={{ flex: 1 }} />

        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('stateInspector:toggle'))}
          style={headerBtnStyle}
          title="State inspector & report an issue"
          aria-label="State inspector and report an issue"
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <Bug size={18} />
        </button>

        {showConnectBroker && <ConnectBrokerHeaderButton />}

        {user && hasTradeAccess && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <TradeJournalLockoutActions variant="header" />
          </div>
        )}

        <button
          type="button"
          onClick={() => setSidebarCollapsed(prev => !prev)}
          style={headerBtnStyle}
          title={sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'}
          aria-label={sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          {sidebarCollapsed ? <List size={20} /> : <X size={20} />}
        </button>
      </header>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <main
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: 0,
            position: 'relative',
          }}
        >
          <Suspense fallback={<DashboardOutletFallback colors={colors} />}>
            <Outlet />
          </Suspense>
        </main>

        <DashboardSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(true)}
        />
      </div>

      <FloatingPrecisionAIChat sidebarWidth={sidebarCollapsed ? 0 : SIDEBAR_WIDTH} />

      <TradeRiskSetupModal />
      <TickerInputModal />
    </div>
  );
}
